import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Wrench, Eye, EyeOff, AlertCircle, Check, X, Loader2, Users, Gift, Tag, Ticket } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signup } = useAuth();
  const [step, setStep] = useState(1);
  const [partnerCode, setPartnerCode] = useState(searchParams.get("partner") || "");
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [partnerValidating, setPartnerValidating] = useState(false);
  const [userReferralCode] = useState(searchParams.get("ref") || "");
  const [referrerInfo, setReferrerInfo] = useState(null);
  // Show input expanded only if URL has partner param, otherwise show collapsed link
  const [showPartnerInput, setShowPartnerInput] = useState(false);
  
  // Coupon code state
  const [couponCode, setCouponCode] = useState(searchParams.get("coupon") || "");
  const [couponInfo, setCouponInfo] = useState(null);
  const [couponValidating, setCouponValidating] = useState(false);
  const [showCouponInput, setShowCouponInput] = useState(false);
  
  const [formData, setFormData] = useState({
    company_name: "",
    subdomain: "",
    phone: "",
    address: "",
    admin_name: "",
    admin_email: "",
    admin_password: "",
    confirm_password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [subdomainStatus, setSubdomainStatus] = useState({ checking: false, available: null });

  // Validate user referral code on mount
  useEffect(() => {
    if (userReferralCode) {
      axios.get(`${API}/referrals/validate/${userReferralCode}`)
        .then(res => {
          if (res.data.valid) {
            setReferrerInfo(res.data);
          }
        })
        .catch(() => {});
    }
  }, [userReferralCode]);

  // Validate partner code on mount if from URL
  useEffect(() => {
    if (partnerCode && searchParams.get("partner")) {
      validatePartnerCode(partnerCode, true);
    }
  }, []);

  const validatePartnerCode = async (code, fromUrl = false) => {
    if (!code || code.length < 3) {
      setPartnerInfo(null);
      return;
    }
    
    setPartnerValidating(true);
    try {
      const res = await axios.get(`${API}/reseller/validate/${code.toUpperCase()}`);
      if (res.data.valid) {
        setPartnerInfo(res.data);
        setPartnerCode(code.toUpperCase());
      } else {
        setPartnerInfo({ valid: false, error: "Invalid partner code" });
        // If from URL and invalid, show the input so user can see the error
        if (fromUrl) {
          setShowPartnerInput(true);
        }
      }
    } catch (err) {
      setPartnerInfo({ valid: false, error: "Invalid partner code" });
      if (fromUrl) {
        setShowPartnerInput(true);
      }
    } finally {
      setPartnerValidating(false);
    }
  };

  const handlePartnerCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setPartnerCode(code);
    setPartnerInfo(null);
  };

  const handleApplyPartnerCode = () => {
    if (partnerCode) {
      validatePartnerCode(partnerCode);
    }
  };

  // Validate coupon code on mount if from URL
  useEffect(() => {
    if (couponCode && searchParams.get("coupon")) {
      validateCouponCode(couponCode, true);
    }
  }, []);

  const validateCouponCode = async (code, fromUrl = false) => {
    if (!code || code.length < 3) {
      setCouponInfo(null);
      return;
    }
    
    setCouponValidating(true);
    try {
      const res = await axios.get(`${API}/coupons/validate-public/${code.toUpperCase()}`);
      if (res.data.valid) {
        setCouponInfo(res.data);
        setCouponCode(code.toUpperCase());
      } else {
        setCouponInfo({ valid: false, error: res.data.error || "Invalid coupon code" });
        if (fromUrl) {
          setShowCouponInput(true);
        }
      }
    } catch (err) {
      setCouponInfo({ valid: false, error: "Invalid coupon code" });
      if (fromUrl) {
        setShowCouponInput(true);
      }
    } finally {
      setCouponValidating(false);
    }
  };

  const handleCouponCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setCouponCode(code);
    setCouponInfo(null);
  };

  const handleApplyCouponCode = () => {
    if (couponCode) {
      validateCouponCode(couponCode);
    }
  };

  useEffect(() => {
    const checkSubdomain = async () => {
      if (formData.subdomain.length < 3) {
        setSubdomainStatus({ checking: false, available: null });
        return;
      }

      setSubdomainStatus({ checking: true, available: null });
      try {
        const response = await axios.get(`${API}/tenants/check-subdomain/${formData.subdomain}`);
        setSubdomainStatus({ checking: false, available: response.data.available });
      } catch {
        setSubdomainStatus({ checking: false, available: null });
      }
    };

    const debounce = setTimeout(checkSubdomain, 500);
    return () => clearTimeout(debounce);
  }, [formData.subdomain]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "subdomain") {
      // Only allow lowercase letters, numbers, and hyphens
      const sanitized = value.toLowerCase().replace(/[^a-z0-9-]/g, "");
      setFormData({ ...formData, [name]: sanitized });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const validateStep1 = () => {
    if (!formData.company_name.trim()) {
      setError("Company name is required");
      return false;
    }
    if (formData.subdomain.length < 3) {
      setError("Subdomain must be at least 3 characters");
      return false;
    }
    if (!subdomainStatus.available) {
      setError("Subdomain is not available");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.admin_name.trim()) {
      setError("Your name is required");
      return false;
    }
    if (!formData.admin_email.trim()) {
      setError("Email is required");
      return false;
    }
    if (formData.admin_password.length < 6) {
      setError("Password must be at least 6 characters");
      return false;
    }
    if (formData.admin_password !== formData.confirm_password) {
      setError("Passwords do not match");
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    setError("");
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validateStep2()) return;

    setLoading(true);
    try {
      await signup({
        company_name: formData.company_name,
        subdomain: formData.subdomain,
        phone: formData.phone,
        address: formData.address,
        admin_name: formData.admin_name,
        admin_email: formData.admin_email,
        admin_password: formData.admin_password,
        partner_code: partnerCode || undefined,
        user_referral_code: userReferralCode || undefined,
        coupon_code: couponCode || undefined,
      });
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
            <Wrench className="w-6 h-6 text-primary-foreground" />
          </div>
          <span className="text-2xl font-bold tracking-tight">
            aftersales<span className="text-primary">.pro</span>
          </span>
        </div>

        <Card className="card-shadow">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Start your free trial</CardTitle>
            <CardDescription>14 days free, no credit card required</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Partner referral badge or input */}
            {partnerInfo?.valid ? (
              <div className="flex items-center justify-center gap-2 mb-4 py-2 px-4 bg-purple-50 rounded-lg border border-purple-200">
                <Users className="w-4 h-4 text-purple-600" />
                <span className="text-sm text-purple-700">Partner: <strong>{partnerInfo.reseller_name}</strong></span>
                <Check className="w-4 h-4 text-green-500" />
              </div>
            ) : (
              <div className="mb-4">
                {!showPartnerInput ? (
                  <button
                    type="button"
                    onClick={() => setShowPartnerInput(true)}
                    className="text-sm text-primary hover:underline flex items-center gap-1 mx-auto"
                  >
                    <Tag className="w-3 h-3" />
                    Have a partner/referral code?
                  </button>
                ) : (
                  <div className="space-y-2 p-3 bg-muted/50 rounded-lg border">
                    <Label className="text-sm flex items-center gap-1">
                      <Gift className="w-3 h-3" />
                      Partner Code (Optional)
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        placeholder="Enter partner code"
                        value={partnerCode}
                        onChange={handlePartnerCodeChange}
                        className="uppercase"
                        data-testid="partner-code-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleApplyPartnerCode}
                        disabled={partnerValidating || !partnerCode}
                        data-testid="apply-partner-code-btn"
                      >
                        {partnerValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                      </Button>
                    </div>
                    {partnerInfo?.valid === false && (
                      <p className="text-xs text-destructive flex items-center gap-1">
                        <X className="w-3 h-3" />
                        {partnerInfo.error || "Invalid code"}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* User referral badge */}
            {referrerInfo && !partnerCode && (
              <div className="flex items-center justify-center gap-2 mb-4 py-2 px-4 bg-green-50 rounded-lg border border-green-200">
                <Users className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-700">
                  Referred by <strong>{referrerInfo.referrer_name}</strong> - Get 30 days free on first subscription!
                </span>
              </div>
            )}
            
            {/* Coupon code input */}
            {couponInfo?.valid ? (
              <div className="flex items-center justify-center gap-2 mb-4 py-2 px-4 bg-amber-50 rounded-lg border border-amber-200">
                <Ticket className="w-4 h-4 text-amber-600" />
                <span className="text-sm text-amber-700">
                  Coupon: <strong>{couponInfo.code}</strong> - 
                  {couponInfo.coupon_type === "percentage" && ` ${couponInfo.value}% off`}
                  {couponInfo.coupon_type === "fixed_amount" && ` ₹${couponInfo.value} off`}
                  {couponInfo.coupon_type === "free_days" && ` ${couponInfo.value} days free`}
                </span>
                <Check className="w-4 h-4 text-green-500" />
              </div>
            ) : (
              <div className="mb-4">
                {!showCouponInput ? (
                  <button
                    type="button"
                    onClick={() => setShowCouponInput(true)}
                    className="text-sm text-amber-600 hover:underline flex items-center gap-1 mx-auto"
                  >
                    <Ticket className="w-3 h-3" />
                    Have a coupon code?
                  </button>
                ) : (
                  <div className="space-y-2 p-3 bg-amber-50/50 rounded-lg border border-amber-100">
                    <Label className="text-sm flex items-center gap-1">
                      <Ticket className="w-3 h-3" />
                      Coupon Code (Optional)
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        placeholder="Enter coupon code"
                        value={couponCode}
                        onChange={handleCouponCodeChange}
                        className="uppercase"
                        data-testid="coupon-code-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleApplyCouponCode}
                        disabled={couponValidating || !couponCode}
                        data-testid="apply-coupon-code-btn"
                      >
                        {couponValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                      </Button>
                    </div>
                    {couponInfo?.valid === false && (
                      <p className="text-xs text-destructive flex items-center gap-1">
                        <X className="w-3 h-3" />
                        {couponInfo.error || "Invalid code"}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Progress indicator */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className={`flex items-center gap-2 ${step >= 1 ? "text-primary" : "text-muted-foreground"}`}>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step >= 1 ? "bg-primary text-primary-foreground" : "bg-muted"
                  }`}
                >
                  1
                </div>
                <span className="text-sm hidden sm:inline">Shop Info</span>
              </div>
              <div className={`w-12 h-0.5 ${step >= 2 ? "bg-primary" : "bg-muted"}`} />
              <div className={`flex items-center gap-2 ${step >= 2 ? "text-primary" : "text-muted-foreground"}`}>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step >= 2 ? "bg-primary text-primary-foreground" : "bg-muted"
                  }`}
                >
                  2
                </div>
                <span className="text-sm hidden sm:inline">Account</span>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm mb-4">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Shop / Company Name</Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    type="text"
                    placeholder="NeoStore Repairs"
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                    data-testid="signup-company-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subdomain">Choose your subdomain</Label>
                  <div className="flex">
                    <Input
                      id="subdomain"
                      name="subdomain"
                      type="text"
                      placeholder="neostore"
                      value={formData.subdomain}
                      onChange={handleChange}
                      className="rounded-r-none"
                      required
                      data-testid="signup-subdomain-input"
                    />
                    <div className="flex items-center px-3 bg-muted border border-l-0 border-input rounded-r-lg text-sm text-muted-foreground">
                      .aftersales.pro
                    </div>
                  </div>
                  {formData.subdomain.length >= 3 && (
                    <div className="flex items-center gap-2 text-sm">
                      {subdomainStatus.checking ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                          <span className="text-muted-foreground">Checking availability...</span>
                        </>
                      ) : subdomainStatus.available === true ? (
                        <>
                          <Check className="w-4 h-4 text-green-500" />
                          <span className="text-green-500">Available!</span>
                        </>
                      ) : subdomainStatus.available === false ? (
                        <>
                          <X className="w-4 h-4 text-destructive" />
                          <span className="text-destructive">Already taken</span>
                        </>
                      ) : null}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Mobile Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="9876543210"
                    value={formData.phone}
                    onChange={handleChange}
                    data-testid="signup-phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Shop Address</Label>
                  <Input
                    id="address"
                    name="address"
                    type="text"
                    placeholder="123, Main Street, City"
                    value={formData.address}
                    onChange={handleChange}
                    data-testid="signup-address-input"
                  />
                </div>

                <Button
                  type="button"
                  className="w-full"
                  onClick={handleNextStep}
                  disabled={!subdomainStatus.available}
                  data-testid="signup-next-btn"
                >
                  Next Step
                </Button>
              </div>
            )}

            {step === 2 && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="admin_name">Your Name</Label>
                  <Input
                    id="admin_name"
                    name="admin_name"
                    type="text"
                    placeholder="John Doe"
                    value={formData.admin_name}
                    onChange={handleChange}
                    required
                    data-testid="signup-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="admin_email">Email Address</Label>
                  <Input
                    id="admin_email"
                    name="admin_email"
                    type="email"
                    placeholder="john@neostore.com"
                    value={formData.admin_email}
                    onChange={handleChange}
                    required
                    data-testid="signup-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="admin_password">Password</Label>
                  <div className="relative">
                    <Input
                      id="admin_password"
                      name="admin_password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.admin_password}
                      onChange={handleChange}
                      required
                      data-testid="signup-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Confirm Password</Label>
                  <Input
                    id="confirm_password"
                    name="confirm_password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.confirm_password}
                    onChange={handleChange}
                    required
                    data-testid="signup-confirm-password-input"
                  />
                </div>

                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1">
                    Back
                  </Button>
                  <Button type="submit" className="flex-1" disabled={loading} data-testid="signup-submit-btn">
                    {loading ? (
                      <>
                        <span className="spinner mr-2" />
                        Creating...
                      </>
                    ) : (
                      "Create Account"
                    )}
                  </Button>
                </div>
              </form>
            )}

            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">Already have an account? </span>
              <Link to="/login" className="text-primary hover:underline" data-testid="signup-login-link">
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
            ← Back to homepage
          </Link>
        </div>
      </div>
    </div>
  );
}
